import { useMemo } from 'react'

import { currentPageParams } from 'scrivito'

import { SavedFilterStructure } from '@/Atomic/Organisms/FilterBuilder'
import { useSavedFilters, SavedFilter } from '@/hooks/useSavedFilters'
import { writeUrlParams, UrlParamsPatch } from '@/hooks/useUrlParams'
import { ensureString } from '@/utils/ensureString'
import { FILTER_PARAM_PREFIX } from '@/utils/filterParams'
import { encodeFilterQuery, decodeFilterQuery } from '@/utils/filterQuery'
import { FilterUsageStorage } from '@/utils/filterUsageStorage'

/**
 * The advanced filter applied to the current list, compiled from the URL alone:
 *
 * - `?filter=<id>`  → a saved filter is selected; it drives the list.
 * - `?query=<b64>`  → no saved filter selected, so the unsaved draft drives it.
 * - both present    → the saved filter wins; the draft is retained so the user
 *                     can switch back to it (see `EntityListContext`, which
 *                     applies exactly one of the two params).
 *
 * There is no component state anywhere in this flow — every value below is a
 * derivation of `currentPageParams()`, which is reactive under `connect()`.
 * Callers MUST be `connect()`-wrapped.
 */
export interface ActiveFilter {
  /** `?filter=` — empty string when no saved filter is selected. */
  selectedFilterId: string
  /** `?query=` decoded — the unsaved advanced filter, if one exists. */
  draft: SavedFilterStructure | undefined
  /** Which of the two is actually filtering the list right now. */
  activeSource: 'saved' | 'draft' | 'none'
}

/**
 * Decode `?query=` once per distinct param value.
 *
 * The memo is not an optimization — it is a correctness requirement. Decoding
 * builds a new object every call (`JSON.parse`), and consumers put the result in
 * effect dependency arrays: `AdvancedSearchModal` seeds the builder from it in an
 * effect that calls `setFilter`. A fresh identity each render makes that effect
 * re-fire on its own setState, which locks up the tab.
 *
 * Split out so the dependency is a plain string parameter. Inline in
 * `useActiveFilter` the dep reads off the `currentPageParams()` object, which
 * React Compiler treats as mutable and then refuses to memoize on.
 *
 * DO NOT drop this memo. `vitest/hooks/useActiveFilter.test.ts` asserts the
 * identity is stable across re-renders, and that test fails without it.
 *
 * This is a workaround, not the real fix — see the seeding effect in
 * `AdvancedSearchModal`, which is what actually needs replacing.
 */
const useDecodedFilterQuery = (query: string) =>
  useMemo(() => decodeFilterQuery(query), [query])

export const useActiveFilter = (entityName: string) => {
  const params = currentPageParams()
  const selectedFilterId = ensureString(params['filter'] ?? null)
  const draft = useDecodedFilterQuery(ensureString(params['query'] ?? null))

  const savedFilters = useSavedFilters(entityName)

  const activeSource: ActiveFilter['activeSource'] = selectedFilterId
    ? 'saved'
    : draft
      ? 'draft'
      : 'none'

  /**
   * Select a saved filter. The draft `query` is deliberately left in the URL so
   * the "Unsaved" row stays available to switch back to; `EntityListContext`
   * ignores it while a saved filter is selected.
   */
  const selectFilter = (id: string) => {
    writeUrlParams({ filter: id || null })
    if (id) {
      FilterUsageStorage.save(entityName, id, Date.now())
    }
  }

  /** Deselect the saved filter, handing the list back to the unsaved draft. */
  const selectDraft = () => {
    writeUrlParams({ filter: null })
  }

  /** Apply an edited/new draft — replaces the draft and deselects any saved filter. */
  const applyDraft = (structure: SavedFilterStructure) => {
    writeUrlParams({ query: encodeFilterQuery(structure), filter: null })
  }

  /**
   * Select a filter that has just been persisted. The draft was *promoted* to a
   * named filter, so it stops existing.
   */
  const selectSavedFilter = (id: string) => {
    writeUrlParams({ filter: id, query: null })
    FilterUsageStorage.save(entityName, id, Date.now())
  }

  /**
   * Discard the unsaved draft without touching the saved-filter selection. The
   * "Unsaved" row is derived from `?query=`, so dropping the param removes the
   * row too. If the draft was the active filter, the list goes unfiltered; if a
   * saved filter was selected, it stays selected and keeps driving the list.
   */
  const discardDraft = () => {
    writeUrlParams({ query: null })
  }

  /** Drop the advanced filter entirely (both params), keeping quick filters. */
  const clearAdvancedFilter = () => {
    writeUrlParams({ query: null, filter: null })
  }

  /** Drop every filter: advanced, labels, and all committed `f_*` quick filters. */
  const clearAllFilters = () => {
    // ONE write nulling everything, so no second navigateTo can clobber it.
    const patch: UrlParamsPatch = { query: null, filter: null, labels: null }
    for (const key of Object.keys(currentPageParams())) {
      if (key.startsWith(FILTER_PARAM_PREFIX)) {
        patch[key] = null
      }
    }
    writeUrlParams(patch)
  }

  return {
    selectedFilterId,
    draft,
    activeSource,
    selectFilter,
    selectDraft,
    applyDraft,
    discardDraft,
    selectSavedFilter,
    clearAdvancedFilter,
    clearAllFilters,
    ...savedFilters,
  }
}

export type UseActiveFilterReturn = ReturnType<typeof useActiveFilter>
export type { SavedFilter }
